<script setup lang="ts">
  import type { StateMachineContext } from '~/models/StateMachineModels'
  import { SessionStore } from '~/stores/session'
  import { SignInResult } from '~/generated/api-clients-generated'
  import type { HpForm } from '#components'
  import type { PushToSaveScenarios } from '~/composables/PushToSave'

  const props = defineProps<{
    context: StateMachineContext
  }>()

  const { signIn } = Auth.use()
  const session = SessionStore.use()

  const scenario = computed(() => (props.context?.config ?? 'add-needs') as PushToSaveScenarios)
  const { data } = await PushToSaveContent.use(scenario.value)

  const screen = computed(() => data.value?.screens?.[props.context.current])

  const isRetry = computed(() => props.context.current === 'retry')
  const backRoute = props.context.data.get('backRoute')
  const loading = ref<boolean>(false)

  const form = ref<InstanceType<typeof HpForm> | null>(null)

  const onSubmit = async () => {
    loading.value = true

    const response = await signIn()

    switch (response.result) {
      case SignInResult.CodeSent:
        props.context.next()
        break
      case SignInResult.DoesNotExist:
        form.value?.setErrors({
          'account-identifier': screen.value?.errors?.noAccount ?? 'An error occurred'
        })
        break
      default:
        form.value?.setErrors({ 'account-identifier': 'An error occurred' })
        break
    }

    loading.value = false
  }

  const { signInValidation } = AccountValidation.use()

  const onNoAccount = () => {
    props.context.goTo('signUp')
  }
</script>

<template>
  <div v-if="!_isNil(backRoute)" class="mt-[-30px] flex justify-start">
    <HpAnchor class="hover-bg-active" @click.prevent="props.context.goTo(backRoute)">
      <HpIcon name="arrow-left" />
    </HpAnchor>
  </div>

  <div class="mb-7 grid place-content-center place-items-center gap-3 text-center">
    <span class="text-5xl leading-none">{{ screen?.icon }}</span>
    <h2>{{ screen?.heading }}</h2>
    <p v-if="screen?.text" v-html="screen.text" class="text-balance leading-snug"></p>
  </div>

  <HpForm
    id="sign-in-form"
    ref="form"
    :validation-schema="signInValidation"
    :on-submit="onSubmit"
    class="flex w-full flex-col items-start gap-y-4"
  >
    <HpFormInput
      id="account-identifier"
      :label="data?.labels?.identifier"
      :placeholder="data?.labels?.identifier"
      v-model="session.authBag.identifier"
      class="w-full"
    />

    <p v-if="!isRetry" class="w-full text-balance text-center leading-normal">
      {{ screen?.signUpOption?.text }}
      <HpAnchor @click.prevent="onNoAccount" class="font-semibold">
        {{ screen?.signUpOption?.cta }}
      </HpAnchor>
    </p>

    <HpButton class="mt-12" :busy="loading">Continue</HpButton>
  </HpForm>
</template>

<style scoped></style>
